/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useRef, useState } from 'react';
import { 
  Player, Enemy, Projectile, Particle, PowerUp, GameControls, 
  DamageIndicator, EnemyType, PowerUpType, GameDifficulty, GameScreen
} from '../types';
import { 
  playShoot, playMissileLaunch, playExplosion, playPowerUp, 
  playWarningAlarm, playHit, playEmp 
} from '../utils/audio';

interface GameCanvasProps {
  difficulty: GameDifficulty;
  screen: GameScreen;
  setScreen: (screen: GameScreen) => void;
  score: number;
  setScore: (score: number) => void;
  bestScore: number;
  setBestScore: (score: number) => void;
  kills: number;
  setKills: (kills: number) => void;
  level: number;
  setLevel: (level: number) => void;
  resetRequested: boolean;
  onResetComplete: () => void;
  onPlayerStatsUpdate: (health: number, maxHealth: number, shield: number, maxShield: number, missiles: number, maxMissiles: number, xp: number, xpNeeded: number, combo: number) => void;
}

export const GameCanvas: React.FC<GameCanvasProps> = ({
  difficulty,
  screen,
  setScreen,
  score,
  setScore,
  bestScore,
  setBestScore,
  kills,
  setKills,
  level,
  setLevel,
  resetRequested,
  onResetComplete,
  onPlayerStatsUpdate,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Core dimensions
  const [dimensions, setDimensions] = useState({ width: 1024, height: 576 });

  // Game references to avoid React state re-render latency in game loop
  const stateRef = useRef<{
    player: Player;
    enemies: Enemy[];
    projectiles: Projectile[];
    particles: Particle[];
    powerUps: PowerUp[];
    damageIndicators: DamageIndicator[];
    controls: GameControls;
    lastTime: number;
    spawnTimer: number;
    powerUpSpawnTimer: number;
    bossActive: boolean;
    bossIncomingTimer: number; // For sirens before boss
    bgScroll: number;
    starScroll: number;
    shakeIntensity: number;
    difficultyMultiplier: number;
    autoMissileTimer: number;
  }>({
    player: {
      x: 100,
      y: 288,
      width: 50,
      height: 30,
      health: 100,
      maxHealth: 100,
      shield: 50,
      maxShield: 50,
      missiles: 5,
      maxMissiles: 10,
      score: 0,
      bestScore: 0,
      kills: 0,
      xp: 0,
      xpNeeded: 100,
      level: 1,
      combo: 1,
      comboTimer: 0,
      speedBoostActive: false,
      speedBoostTimer: 0,
      shieldActive: true,
      invincibilityActive: false,
      invincibilityTimer: 0,
      rapidFireActive: false,
      rapidFireTimer: 0,
      tripleMissilesActive: false,
      tripleMissilesTimer: 0,
    },
    enemies: [],
    projectiles: [],
    particles: [],
    powerUps: [],
    damageIndicators: [],
    controls: {
      up: false,
      down: false,
      left: false,
      right: false,
      shift: false,
      space: false,
    },
    lastTime: 0,
    spawnTimer: 0,
    powerUpSpawnTimer: 0,
    bossActive: false,
    bossIncomingTimer: 0,
    bgScroll: 0,
    starScroll: 0,
    shakeIntensity: 0,
    difficultyMultiplier: 1,
    autoMissileTimer: 0,
  });

  // Track the locked target's ID
  const [lockedTargetId, setLockedTargetId] = useState<string | null>(null);

  // Sync state to ref on component mount or reset
  const initGame = () => {
    let diffMult = 1.0;
    if (difficulty === 'veteran') diffMult = 1.4;
    if (difficulty === 'ace') diffMult = 1.8;

    stateRef.current = {
      player: {
        x: 100,
        y: dimensions.height / 2,
        width: 55,
        height: 35,
        health: 100,
        maxHealth: 100,
        shield: 50,
        maxShield: 50,
        missiles: 5,
        maxMissiles: 10,
        score: 0,
        bestScore: parseInt(localStorage.getItem('airstrip_best') || '0', 10),
        kills: 0,
        xp: 0,
        xpNeeded: 100,
        level: 1,
        combo: 1,
        comboTimer: 0,
        speedBoostActive: false,
        speedBoostTimer: 0,
        shieldActive: true,
        invincibilityActive: false,
        invincibilityTimer: 0,
        rapidFireActive: false,
        rapidFireTimer: 0,
        tripleMissilesActive: false,
        tripleMissilesTimer: 0,
      },
      enemies: [],
      projectiles: [],
      particles: [],
      powerUps: [],
      damageIndicators: [],
      controls: {
        up: false,
        down: false,
        left: false,
        right: false,
        shift: false,
        space: false,
      },
      lastTime: performance.now(),
      spawnTimer: 0,
      powerUpSpawnTimer: 0,
      bossActive: false,
      bossIncomingTimer: 0,
      bgScroll: 0,
      starScroll: 0,
      shakeIntensity: 0,
      difficultyMultiplier: diffMult,
      autoMissileTimer: 0,
    };

    setLockedTargetId(null);
    setScore(0);
    setKills(0);
    setLevel(1);
    
    // Fire initial callback for HUD
    const p = stateRef.current.player;
    onPlayerStatsUpdate(p.health, p.maxHealth, p.shield, p.maxShield, p.missiles, p.maxMissiles, p.xp, p.xpNeeded, p.combo);
  };

  // Keyboard Event Handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (screen !== 'playing') return;

      const keys: Record<string, boolean> = {};
      const { controls } = stateRef.current;

      if (e.key === 'w' || e.key === 'W' || e.key === 'ArrowUp') controls.up = true;
      if (e.key === 's' || e.key === 'S' || e.key === 'ArrowDown') controls.down = true;
      if (e.key === 'a' || e.key === 'A' || e.key === 'ArrowLeft') controls.left = true;
      if (e.key === 'd' || e.key === 'D' || e.key === 'ArrowRight') controls.right = true;
      if (e.key === 'Shift') controls.shift = true;
      
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        // Fire EMP or Lock-On Homing Missiles manually on space bar press (burst fire)
        triggerSpecialMissile();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const { controls } = stateRef.current;
      if (e.key === 'w' || e.key === 'W' || e.key === 'ArrowUp') controls.up = false;
      if (e.key === 's' || e.key === 'S' || e.key === 'ArrowDown') controls.down = false;
      if (e.key === 'a' || e.key === 'A' || e.key === 'ArrowLeft') controls.left = false;
      if (e.key === 'd' || e.key === 'D' || e.key === 'ArrowRight') controls.right = false;
      if (e.key === 'Shift') controls.shift = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [screen, dimensions]);

  // Handle manual/special missile press
  const triggerSpecialMissile = () => {
    const { player, enemies, projectiles } = stateRef.current;
    if (player.missiles <= 0) {
      // Empty charge sound or alarm indication
      playHit();
      return;
    }

    // Find targets to home onto. High priority first, or just closest
    const activeTargets = enemies.filter(e => e.x < dimensions.width + 50 && e.x > -50);
    if (activeTargets.length === 0) return;

    // Launch a special triple rocket set if triple missile is active, or a single heavy seeker
    playMissileLaunch();
    player.missiles -= 1;

    // Find closest target
    let nearest = activeTargets[0];
    let minDist = Infinity;
    activeTargets.forEach(e => {
        const d = Math.hypot(e.x - player.x, e.y - player.y);
        if (d < minDist) {
          minDist = d;
          nearest = e;
        }
    });

    if (player.tripleMissilesActive) {
      // 3 homing missiles spread
      for (let i = -1; i <= 1; i++) {
        projectiles.push({
          id: Math.random().toString(36).substring(2, 9),
          type: 'missile',
          x: player.x + player.width,
          y: player.y + player.height / 2,
          vx: 4,
          vy: i * 2,
          width: 25,
          height: 10,
          damage: 35,
          angle: i * 0.2,
          targetId: nearest.id,
        });
      }
    } else {
      // Single solid precision homing rocket
      projectiles.push({
        id: Math.random().toString(36).substring(2, 9),
        type: 'missile',
        x: player.x + player.width,
        y: player.y + player.height / 2,
        vx: 6,
        vy: 0,
        width: 25,
        height: 10,
        damage: 45,
        angle: 0,
        targetId: nearest.id,
      });
    }

    onPlayerStatsUpdate(
      player.health, player.maxHealth, player.shield, player.maxShield, 
      player.missiles, player.maxMissiles, player.xp, player.xpNeeded, player.combo
    );
  };

  // Resize handler
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        // Maintain a standard 16:9 arcade gaming proportion inside viewport bounds
        const height = Math.min(window.innerHeight * 0.70, width * 0.5625);
        setDimensions({ width, height });
      }
    };

    handleResize();
    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);

  // Watch for game reset request
  useEffect(() => {
    if (resetRequested) {
      initGame();
      onResetComplete();
    }
  }, [resetRequested]);

  // Initial startup initialization
  useEffect(() => {
    initGame();
  }, [difficulty]);

  // Main game loop
  useEffect(() => {
    let animationId: number;
    let localTimer = 0;

    const gameLoop = (timestamp: number) => {
      if (screen !== 'playing') {
        animationId = requestAnimationFrame(gameLoop);
        return;
      }

      const { lastTime } = stateRef.current;
      const deltaTime = Math.min(timestamp - lastTime, 100); // Caps delta time at 100ms
      stateRef.current.lastTime = timestamp;

      // Update game objects
      updateGame(deltaTime);

      // Render onto canvas
      renderGame();

      // Repeat loop
      animationId = requestAnimationFrame(gameLoop);
    };

    animationId = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animationId);
  }, [screen, dimensions]);

  // Helper: Damage floaty indicator popup
  const addDamageIndicator = (x: number, y: number, text: string, color: string) => {
    stateRef.current.damageIndicators.push({
      id: Math.random().toString(36).substring(2, 9),
      x,
      y,
      text,
      color,
      alpha: 1.0,
      life: 800, // 800ms
    });
  };

  // Helper: Create smoke and fire particles at exact coordinate
  const createExplosionEffect = (x: number, y: number, intenseLevel: 'small' | 'large' | 'boss') => {
    const { particles } = stateRef.current;
    let count = 12;
    let sizeRange = 4;
    let speedMult = 2;

    if (intenseLevel === 'large') {
      count = 25;
      sizeRange = 7;
      speedMult = 3.5;
      playExplosion('large');
    } else if (intenseLevel === 'boss') {
      count = 60;
      sizeRange = 12;
      speedMult = 5;
      playExplosion('boss');
    } else {
      playExplosion('small');
    }

    // Spark particles (fast, bright orange/yellow)
    for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = (Math.random() * speedMult + 1);
        particles.push({
          id: Math.random().toString(36).substring(2, 9),
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: Math.random() * sizeRange + 2,
          color: i % 2 === 0 ? '#ff5500' : '#ffea00',
          alpha: 1.0,
          life: 0,
          maxLife: Math.random() * 20 + 15,
          type: 'spark',
        });
    }

    // Fire & Smoke puffs (slow, fading gray/dark orange)
    const puffCount = Math.floor(count / 2);
    for (let i = 0; i < puffCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (Math.random() * speedMult * 0.5 + 0.2);
      particles.push({
        id: Math.random().toString(36).substring(2, 9),
        x,
        y,
        vx: Math.cos(angle) * speed - 0.5, // slightly drift leftward
        vy: Math.sin(angle) * speed,
        size: Math.random() * (sizeRange * 1.5) + 6,
        color: i % 3 === 0 ? '#3a3a3a' : i % 3 === 1 ? '#ff5b00' : '#555555',
        alpha: 0.8,
        life: 0,
        maxLife: Math.random() * 40 + 30,
        type: 'smoke',
      });
    }

    // Metal debris sparks
    if (intenseLevel !== 'small') {
      for (let i = 0; i < 5; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = (Math.random() * speedMult * 0.8 + 1);
        particles.push({
          id: Math.random().toString(36).substring(2, 9),
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: Math.random() * 3 + 1,
          color: '#8c959c',
          alpha: 1.0,
          life: 0,
          maxLife: 50,
          type: 'debris',
        });
      }
    }
  };

  // Spawn enemy handler
  const spawnEnemy = () => {
    const { enemies, difficultyMultiplier, bossActive } = stateRef.current;
    
    // Do not spawn standard enemies if boss warning is counting down or boss is live
    if (bossActive) return;

    // Build specific list of unlockable enemies based on level
    const availableTypes: EnemyType[] = ['basic_drone'];
    if (level >= 2) availableTypes.push('helicopter');
    if (level >= 3) availableTypes.push('fighter');
    if (level >= 4) availableTypes.push('launcher');
    if (level >= 5) availableTypes.push('tank');

    // Select random type from allowed list
    const r = Math.random();
    let type: EnemyType = 'basic_drone';
    if (r > 0.4 && availableTypes.includes('helicopter')) type = 'helicopter';
    if (r > 0.65 && availableTypes.includes('fighter')) type = 'fighter';
    if (r > 0.8 && availableTypes.includes('launcher')) type = 'launcher';
    if (r > 0.92 && availableTypes.includes('tank')) type = 'tank';

    // Base properties based on type
    let w = 40, h = 40;
    let hp = 15;
    let points = 50;
    let speedX = -(Math.random() * 1.5 + 1.5);
    let speedY = 0;
    let initialY = Math.random() * (dimensions.height * 0.7) + (dimensions.height * 0.1);

    switch(type) {
      case 'basic_drone':
        w = 34; h = 34;
        hp = 12 * difficultyMultiplier;
        points = 50;
        speedX = -(Math.random() * 2 + 2);
        break;
      case 'helicopter':
        w = 48; h = 34;
        hp = 25 * difficultyMultiplier;
        points = 100;
        speedX = -1.5;
        // starts with vertical scanning pattern
        speedY = Math.random() > 0.5 ? 1 : -1;
        break;
      case 'fighter':
        w = 44; h = 32;
        hp = 20 * difficultyMultiplier;
        points = 150;
        speedX = -3.5; // very fast
        break;
      case 'launcher':
        w = 40; h = 44;
        hp = 30 * difficultyMultiplier;
        points = 200;
        speedX = -1.0;
        initialY = Math.max(dimensions.height - 110, Math.random() * 50 + (dimensions.height - 110)); // Near ground
        break;
      case 'tank':
        w = 54; h = 38;
        hp = 50 * difficultyMultiplier;
        points = 300;
        speedX = -0.7; // moves along floor
        initialY = dimensions.height - 50; // strictly grounded
        break;
    }

    enemies.push({
      id: Math.random().toString(36).substring(2, 9),
      type,
      x: dimensions.width + 50,
      y: initialY,
      width: w,
      height: h,
      vx: speedX,
      vy: speedY,
      health: hp,
      maxHealth: hp,
      points,
      shootCooldown: Math.random() * 800 + 1200, // miliseconds between attack shots
      shootTimer: 0,
      angle: 0,
    });
  };

  // Spawn Boss
  const spawnBoss = () => {
    const { enemies, difficultyMultiplier } = stateRef.current;
    stateRef.current.bossActive = true;
    
    const bossHp = 450 * difficultyMultiplier;

    enemies.push({
      id: 'boss_omega',
      type: 'boss',
      x: dimensions.width + 120,
      y: dimensions.height / 2 - 50,
      width: 140,
      height: 95,
      vx: -1.2, // Slide smoothly inside
      vy: 1.0,
      health: bossHp,
      maxHealth: bossHp,
      points: 2500,
      shootCooldown: 800,
      shootTimer: 0,
      angle: 0,
      phase: 1,
      phaseTimer: 0,
      targetY: dimensions.height / 2,
    });

    addDamageIndicator(dimensions.width - 250, dimensions.height / 2, '⚠ BOSS INCOMING ⚠', '#ff003c');
  };

  // Trigger EMP blast: clear all enemy screen bullet shots and deal standard drone damage
  const triggerEmpBlast = () => {
    const { projectiles, enemies, player } = stateRef.current;
    
    playEmp();
    addDamageIndicator(player.x, player.y - 15, 'EMP BLOCKED ENEMY MISSILES!', '#00e5ff');
    
    // Convert all enemy bullets to glowing floating health or shock sparks
    const remaining: Projectile[] = [];
    projectiles.forEach(p => {
      if (p.type === 'enemy_bullet' || p.type === 'enemy_missile') {
        // Explode sparkle at that bullet's spot
        createExplosionEffect(p.x, p.y, 'small');
      } else {
        remaining.push(p);
      }
    });
    stateRef.current.projectiles = remaining;

    // Small shock damage to all visible enemies
    enemies.forEach(e => {
      if (e.x < dimensions.width) {
        e.health -= 15;
        addDamageIndicator(e.x + e.width / 2, e.y, '-15 EMP', '#00e5ff');
      }
    });
  };

  // Trigger Power-Up drop from dead enemy
  const triggerPowerUpDrop = (x: number, y: number) => {
    const { powerUps } = stateRef.current;
    const items: PowerUpType[] = ['rapid_fire', 'triple_missiles', 'shield', 'health_pack', 'emp', 'invincibility'];
    // Pick random weighted
    const r = Math.random();
    let type: PowerUpType = 'health_pack';
    
    if (r < 0.22) type = 'shield';
    else if (r < 0.44) type = 'health_pack';
    else if (r < 0.60) type = 'triple_missiles';
    else if (r < 0.75) type = 'rapid_fire';
    else if (r < 0.88) type = 'emp';
    else type = 'invincibility';

    powerUps.push({
      id: Math.random().toString(36).substring(2, 9),
      type,
      x,
      y,
      width: 25,
      height: 25,
      vx: -1.5,
      vy: Math.sin(Math.random() * Math.PI) * 0.5,
      pulsePhase: 0,
    });
  };

  // Core update cycle
  const updateGame = (dt: number) => {
    const { 
      player, enemies, projectiles, particles, powerUps, 
      damageIndicators, controls, difficultyMultiplier, bossActive 
    } = stateRef.current;

    // 1. SCROLL BACKGROUNDS (Parallax logic)
    // Faster layer for space starfields, slower layer for parallax silhouettes
    stateRef.current.bgScroll = (stateRef.current.bgScroll + (player.speedBoostActive ? 2.5 : 1.0) * (dt/16.66)) % dimensions.width;
    stateRef.current.starScroll = (stateRef.current.starScroll + (player.speedBoostActive ? 4.0 : 1.8) * (dt/16.66)) % dimensions.width;

    // Apply viewport Shake Decay
    if (stateRef.current.shakeIntensity > 0) {
      stateRef.current.shakeIntensity -= 0.3;
    }

    // 2. PLAYER MOVEMENT & POWER-UP TICKERS
    const currentSpeed = (controls.shift ? 6.5 : 4.0) * (dt / 16.66);
    player.speedBoostActive = controls.shift;

    if (controls.up) player.y -= currentSpeed;
    if (controls.down) player.y += currentSpeed;
    if (controls.left) player.x -= currentSpeed;
    if (controls.right) player.x += currentSpeed;

    // Screen bound clamps
    player.x = Math.max(20, Math.min(player.x, dimensions.width * 0.6));
    player.y = Math.max(30, Math.min(player.y, dimensions.height - 50));

    // Power-up count downs
    if (player.invincibilityActive) {
      player.invincibilityTimer -= dt;
      if (player.invincibilityTimer <= 0) player.invincibilityActive = false;
    }
    if (player.rapidFireActive) {
      player.rapidFireTimer -= dt;
      if (player.rapidFireTimer <= 0) player.rapidFireActive = false;
    }
    if (player.tripleMissilesActive) {
      player.tripleMissilesTimer -= dt;
      if (player.tripleMissilesTimer <= 0) player.tripleMissilesActive = false;
    }

    // Shield passive regeneration over details
    if (player.shield < player.maxShield && Math.random() < 0.005) {
      player.shield = Math.min(player.maxShield, player.shield + 1);
    }

    // Combo system ticker
    if (player.combo > 1) {
      player.comboTimer -= dt;
      if (player.comboTimer <= 0) {
        player.combo = 1;
      }
    }

    // Thruster trailing particles based on movement
    if (Math.random() < 0.6) {
      particles.push({
        id: Math.random().toString(36).substring(2, 9),
        x: player.x - 10,
        y: player.y + player.height / 2 + (Math.random() * 8 - 4),
        vx: -(Math.random() * 3 + 4),
        vy: Math.random() * 1 - 0.5,
        size: Math.random() * 3 + 2,
        color: player.speedBoostActive ? '#00e5ff' : '#ff7a00',
        alpha: 0.8,
        life: 0,
        maxLife: Math.random() * 15 + 10,
        type: 'smoke',
      });
    }

    // 3. LOCK-ON / TARGET DETECTOR SYSTEM
    // Look for prime valid target on screen
    let targetCandidate: Enemy | null = null;
    let minXCandidate = Infinity;

    enemies.forEach(e => {
      // Find the enemy furthest ahead but strictly inside viewport bounds
      if (e.x > player.x && e.x < dimensions.width + 10) {
        if (e.x < minXCandidate) {
          minXCandidate = e.x;
          targetCandidate = e;
        }
      }
    });

    if (targetCandidate) {
      const candidate: Enemy = targetCandidate;
      if (lockedTargetId !== candidate.id) {
        setLockedTargetId(candidate.id);
      }
    } else {
      if (lockedTargetId !== null) {
        setLockedTargetId(null);
      }
    }

    // 4. AUTO-FIRE MACHINE GUN ENGINE
    // Fired based on a timer
    const baseFireRate = player.rapidFireActive ? 95 : 190;
    stateRef.current.autoMissileTimer += dt;
    player.speedBoostTimer += dt; // use to tick firing

    if (player.speedBoostTimer >= baseFireRate) {
      player.speedBoostTimer = 0;
      
      // Auto target presence
      const targetEnemy = enemies.find(e => e.id === lockedTargetId);
      
      playShoot();
      
      // Determine angle to shoot
      let fireAngle = 0;
      if (targetEnemy) {
        fireAngle = Math.atan2(targetEnemy.y + targetEnemy.height/2 - (player.y + player.height/2), targetEnemy.x - player.x);
        // limit standard shooting lock index sweep range to prevent shooting directly backwards
        if (fireAngle < -Math.PI / 3) fireAngle = -Math.PI / 3;
        if (fireAngle > Math.PI / 3) fireAngle = Math.PI / 3;
      }

      // Add double lasers or normal bullets
      const dmg = 8;
      projectiles.push({
        id: Math.random().toString(36).substring(2, 9),
        type: 'bullet',
        x: player.x + player.width - 5,
        y: player.y + player.height / 3,
        vx: Math.cos(fireAngle) * 9.5,
        vy: Math.sin(fireAngle) * 9.5,
        width: 14,
        height: 4,
        damage: dmg,
        angle: fireAngle,
      });

      projectiles.push({
        id: Math.random().toString(36).substring(2, 9),
        type: 'bullet',
        x: player.x + player.width - 5,
        y: player.y + (player.height * 2) / 3,
        vx: Math.cos(fireAngle) * 9.5,
        vy: Math.sin(fireAngle) * 9.5,
        width: 14,
        height: 4,
        damage: dmg,
        angle: fireAngle,
      });
    }

    // AUTOMATIC TARGET HOMING MISSILE SPAWNER (Launches free seeker every few seconds if target locked)
    if (stateRef.current.autoMissileTimer >= 2200) {
      stateRef.current.autoMissileTimer = 0;
      const targetEnemy = enemies.find(e => e.id === lockedTargetId);
      if (targetEnemy && player.missiles < player.maxMissiles) {
        // Fire free homing missile
        player.missiles = Math.min(player.maxMissiles, player.missiles + 1);
        onPlayerStatsUpdate(
          player.health, player.maxHealth, player.shield, player.maxShield, 
          player.missiles, player.maxMissiles, player.xp, player.xpNeeded, player.combo
        );
      }
    }

    // 5. ENEMY SPAWNING & DIFFICULTY PROGRESSION ENGINE
    stateRef.current.spawnTimer += dt;
    const baseSpawnRate = Math.max(1200, 3000 - level * 350);

    // If level-up threshold reached, update progression
    const pointsNeeded = player.xpNeeded;
    if (player.xp >= pointsNeeded) {
      player.xp -= pointsNeeded;
      player.level += 1;
      player.xpNeeded = Math.floor(player.xpNeeded * 1.3);
      setLevel(player.level);
      
      // Floating level indicators
      addDamageIndicator(player.x, player.y - 30, '⚡ LEVEL UP: ULTRA ACCESS ⚡', '#00ff66');
      playPowerUp();
      
      // Add missile rewards
      player.missiles = Math.min(player.maxMissiles, player.missiles + 4);

      // Check for incoming Boss fight every 5 levels
      if (player.level % 4 === 0 && !bossActive) {
        spawnBoss();
      }
    }

    if (stateRef.current.spawnTimer >= baseSpawnRate) {
      stateRef.current.spawnTimer = 0;
      spawnEnemy();
    }

    // 6. UPDATE ENEMIES
    const livingEnemies: Enemy[] = [];
    enemies.forEach(e => {
      e.shootTimer += dt;

      // Type behaviors
      if (e.type === 'helicopter') {
        // Oscillates vertically
        e.y += e.vy * (dt / 16.66) * 1.2;
        if (e.y < 40) { e.y = 40; e.vy = 1; }
        if (e.y > dimensions.height - 100) { e.y = dimensions.height - 100; e.vy = -1; }
      } else if (e.type === 'fighter') {
        // Fast flying sine wave pattern
        e.y += Math.sin(e.x * 0.02) * 2.0;
      } else if (e.type === 'boss') {
        // Boss fight states
        e.phaseTimer = (e.phaseTimer || 0) + dt;
        
        // Horizontal entry
        if (e.x > dimensions.width - 180) {
          e.x += e.vx * (dt / 16.66);
        } else {
          // Hover and float
          e.y += e.vy * (dt / 16.66) * 0.8;
          if (e.y < 40 || e.y > dimensions.height - 140) {
            e.vy *= -1;
          }
        }

        // Multiple phases based on health
        const ratio = e.health / e.maxHealth;
        if (ratio < 0.35 && e.phase === 2) {
          e.phase = 3;
          createExplosionEffect(e.x + 50, e.y + 40, 'large');
          addDamageIndicator(e.x, e.y - 15, 'CRITICAL: ANGER THREAT MODE ACTIVE!', '#ff003c');
        } else if (ratio < 0.70 && e.phase === 1) {
          e.phase = 2;
          createExplosionEffect(e.x + 20, e.y + 30, 'large');
          addDamageIndicator(e.x, e.y - 15, 'PHASE 2: ROCKET GATLING BARRIER!', '#ff9c00');
        }
      }

      // Enemy default horizontal progress
      if (e.type !== 'boss') {
        e.x += e.vx * (dt / 16.66);
      }

      // Shoot trigger
      if (e.shootTimer >= e.shootCooldown && e.x < dimensions.width) {
        e.shootTimer = 0;
        
        // Helicopters spray forward
        if (e.type === 'helicopter') {
          projectiles.push({
            id: Math.random().toString(36).substring(2, 9),
            type: 'enemy_bullet',
            x: e.x,
            y: e.y + e.height / 2,
            vx: -5.5,
            vy: 0,
            width: 10,
            height: 4,
            damage: 10,
            angle: Math.PI,
          });
        } 
        // Fighter jet aims specifically at player
        else if (e.type === 'fighter') {
          const aimAngle = Math.atan2(player.y - e.y, player.x - e.x);
          projectiles.push({
            id: Math.random().toString(36).substring(2, 9),
            type: 'enemy_bullet',
            x: e.x,
            y: e.y + e.height / 2,
            vx: Math.cos(aimAngle) * 6.5,
            vy: Math.sin(aimAngle) * 6.5,
            width: 10,
            height: 4,
            damage: 12,
            angle: aimAngle,
          });
        }
        // Base Seekers/Missile Launcher
        else if (e.type === 'launcher') {
          projectiles.push({
            id: Math.random().toString(36).substring(2, 9),
            type: 'enemy_missile',
            x: e.x,
            y: e.y + 5,
            vx: -3.5,
            vy: -1.0,
            width: 18,
            height: 7,
            damage: 18,
            angle: Math.PI + 0.2,
          });
        }
        // Tank fires heavy plasma up shell
        else if (e.type === 'tank') {
          const aimAngle = Math.atan2(player.y - e.y, player.x - e.x);
          projectiles.push({
            id: Math.random().toString(36).substring(2, 9),
            type: 'plasma',
            x: e.x,
            y: e.y + 5,
            vx: Math.cos(aimAngle) * 5.0,
            vy: Math.sin(aimAngle) * 5.0,
            width: 16,
            height: 16,
            damage: 22,
            angle: aimAngle,
          });
        }
        // Boss Barrage modes!
        else if (e.type === 'boss') {
          const bp = e.phase || 1;
          if (bp === 1) {
            // Triple wave
            for (let i = -1; i <= 1; i++) {
              projectiles.push({
                id: Math.random().toString(36).substring(2, 9),
                type: 'enemy_bullet',
                x: e.x,
                y: e.y + e.height / 2 + i * 20,
                vx: -6,
                vy: i * 1.5,
                width: 10,
                height: 4,
                damage: 12,
                angle: Math.PI + i * 0.15,
              });
            }
          } else if (bp === 2) {
            // Rapid wave & Seekers
            projectiles.push({
              id: Math.random().toString(36).substring(2, 9),
              type: 'enemy_bullet',
              x: e.x,
              y: e.y + e.height / 3,
              vx: -7,
              vy: (Math.random() * 2 - 1),
              width: 11,
              height: 4,
              damage: 10,
              angle: Math.PI,
            });
            if (Math.random() < 0.4) {
              projectiles.push({
                id: Math.random().toString(36).substring(2, 9),
                type: 'enemy_missile',
                x: e.x,
                y: e.y + (e.height * 2) / 3,
                vx: -4,
                vy: (player.y - e.y) / 80,
                width: 20,
                height: 8,
                damage: 20,
                angle: Math.PI,
              });
            }
          } else {
            // Chaotic Phase 3: Star circle spray
            for (let i = 0; i < 6; i++) {
              const ang = Math.PI - 0.7 + (i * 1.4) / 5 + Math.sin((e.phaseTimer || 0) * 0.002);
              projectiles.push({
                id: Math.random().toString(36).substring(2, 9),
                type: 'plasma',
                x: e.x,
                y: e.y + e.height / 2,
                vx: Math.cos(ang) * 5.5,
                vy: Math.sin(ang) * 5.5,
                width: 14,
                height: 14,
                damage: 15,
                angle: ang,
              });
            }
          }
        }
      }

      // Check if enemy is off-screen
      if (e.x > -100 && e.health > 0) {
        livingEnemies.push(e);
      } else if (e.health <= 0) {
        // Killed!
        createExplosionEffect(e.x + e.width / 2, e.y + e.height / 2, e.type === 'boss' ? 'boss' : e.type === 'tank' ? 'large' : 'small');
        
        // Add scores & Combo details
        const basePts = e.points;
        const currentComboPts = basePts * player.combo;
        player.score += currentComboPts;
        setScore(player.score);

        // Best highscore check
        if (player.score > player.bestScore) {
          player.bestScore = player.score;
          setBestScore(player.score);
          localStorage.setItem('airstrip_best', player.score.toString());
        }

        player.kills += 1;
        setKills(player.kills);

        // XP drop rewards
        player.xp += Math.floor(basePts * 0.2);

        // Update Combo count
        player.combo += 1;
        player.comboTimer = 3500; // 3.5 seconds reload

        // Trigger float outcome popup
        addDamageIndicator(
          e.x + e.width/2, e.y, 
          `+${currentComboPts} XP${player.combo > 2 ? ` (Combo x${player.combo - 1})` : ''}`, 
          '#ffdd00'
        );

        // Chance to spawn power-up
        const powerUpChance = e.type === 'boss' ? 1.0 : e.type === 'launcher' || e.type === 'tank' ? 0.35 : 0.08;
        if (Math.random() < powerUpChance) {
          triggerPowerUpDrop(e.x + e.width / 2, e.y + e.height / 2);
        }

        if (e.type === 'boss') {
          stateRef.current.bossActive = false;
          addDamageIndicator(e.x, e.y, '🏆 SKY BOSS ELIMINATED 🏆', '#2aff00');
        }
      }
    });
    stateRef.current.enemies = livingEnemies;

    // 7. UPDATE PROJECTILES (Homing rocket logic + physics updates)
    const livingProjectiles: Projectile[] = [];
    projectiles.forEach(p => {
      // If player missile seeks target
      if (p.type === 'missile' && p.targetId) {
        const tgt = enemies.find(e => e.id === p.targetId);
        if (tgt && tgt.health > 0) {
          const dy = (tgt.y + tgt.height / 2) - p.y;
          const dx = (tgt.x + tgt.width / 2) - p.x;
          p.angle = Math.atan2(dy, dx);
          
          // Smooth homing turn adjustment
          const speedFactor = 8.5 * (dt / 16.66);
          p.vx = Math.cos(p.angle) * speedFactor;
          p.vy = Math.sin(p.angle) * speedFactor;
        } else {
          // If locked target is dead, attempt to seek any other visual target on screen
          const alternative = enemies[0];
          if (alternative) {
            p.targetId = alternative.id;
          } else {
            // Keep straight trajectory
            p.vx = 8.5 * (dt / 16.66);
            p.vy = 0;
          }
        }

        // Emit delicate smoke trail particles from missile's exhaust
        if (Math.random() < 0.6) {
          particles.push({
            id: Math.random().toString(36).substring(2, 9),
            x: p.x - Math.cos(p.angle) * 12,
            y: p.y - Math.sin(p.angle) * 12,
            vx: -p.vx * 0.2 + (Math.random() * 1 - 0.5),
            vy: (Math.random() * 1 - 0.5),
            size: Math.random() * 4 + 2,
            color: '#a3a3a3',
            alpha: 0.7,
            life: 0,
            maxLife: 20,
            type: 'smoke',
          });
        }
      }

      // If enemy homing missile traces player
      if (p.type === 'enemy_missile') {
        const dy = player.y + player.height / 2 - p.y;
        const dx = player.x + player.width / 2 - p.x;
        p.angle = Math.atan2(dy, dx);
        
        // Simulating slow homing drift capability
        const missileSpeed = 5.0 * (dt / 16.66);
        p.vx = Math.cos(p.angle) * missileSpeed;
        p.vy = Math.sin(p.angle) * missileSpeed;

        if (Math.random() < 0.4) {
          particles.push({
            id: Math.random().toString(36).substring(2, 9),
            x: p.x,
            y: p.y,
            vx: -p.vx * 0.1,
            vy: 0,
            size: Math.random() * 4 + 1,
            color: '#ff2d00',
            alpha: 0.6,
            life: 0,
            maxLife: 15,
            type: 'smoke',
          });
        }
      }

      // Projectile advancement
      p.x += p.vx;
      p.y += p.vy;

      // Filter screen bounds
      if (p.x > -50 && p.x < dimensions.width + 100 && p.y > -50 && p.y < dimensions.height + 50) {
        livingProjectiles.push(p);
      }
    });
    stateRef.current.projectiles = livingProjectiles;

    // 8. COLLISION DETECTION STAGE
    // Player bullets VS Enemies
    projectiles.forEach(p => {
      if (p.type === 'bullet' || p.type === 'missile') {
        enemies.forEach(e => {
          if (e.health > 0 && 
              p.x > e.x && p.x < e.x + e.width && 
              p.y > e.y && p.y < e.y + e.height) {
            
            // Hit recorded!
            e.health -= p.damage;
            playHit();

            // Destroy bullet
            p.x = -9999; // trigger cull on next tick

            // Add spark hit particles
            for (let i = 0; i < 4; i++) {
              particles.push({
                id: Math.random().toString(36).substring(2, 9),
                x: p.x,
                y: p.y,
                vx: (Math.random() * 4 - 2) - 1.5,
                vy: (Math.random() * 4 - 2),
                size: Math.random() * 2 + 1,
                color: '#ffea00',
                alpha: 1.0,
                life: 0,
                maxLife: 10,
                type: 'spark',
              });
            }

            // Small display float
            addDamageIndicator(e.x + e.width/2, e.y - 12, `${p.damage}`, '#ff4444');
          }
        });
      }

      // Enemy Projectiles vs Player
      if (p.type === 'enemy_bullet' || p.type === 'enemy_missile' || p.type === 'plasma') {
        if (p.x > player.x && p.x < player.x + player.width && 
            p.y > player.y && p.y < player.y + player.height) {

          // Delete projectile
          p.x = -9999;

          if (player.invincibilityActive) {
            addDamageIndicator(player.x, player.y - 15, '🛡 BLOCKED', '#00ffbc');
            return;
          }

          // Camera shake impact
          stateRef.current.shakeIntensity = p.type === 'plasma' ? 12 : p.type === 'enemy_missile' ? 8 : 4;
          playHit();

          let netDmg = p.damage;
          if (p.type === 'enemy_missile' && difficultyMultiplier > 1.2) {
            // Seeker alarm trigger
            playWarningAlarm();
          }

          if (player.shieldActive && player.shield > 0) {
            // Absorb using shields
            player.shield = Math.max(0, player.shield - netDmg);
            addDamageIndicator(player.x, player.y - 10, `-${netDmg} SHIELD`, '#00e5ff');
            
            // Shield bubble visual burst
            for (let i = 0; i < 12; i++) {
              const ang = Math.random() * Math.PI * 2;
              particles.push({
                id: Math.random().toString(36).substring(2, 9),
                x: player.x + player.width/2 + Math.cos(ang) * 30,
                y: player.y + player.height/2 + Math.sin(ang) * 30,
                vx: Math.cos(ang) * 2,
                vy: Math.sin(ang) * 2,
                size: Math.random() * 2 + 2,
                color: '#00dfff',
                alpha: 0.9,
                life: 0,
                maxLife: 15,
                type: 'shield_bubble',
              });
            }
          } else {
            // Apply straight to health
            player.health = Math.max(0, player.health - netDmg);
            addDamageIndicator(player.x, player.y - 10, `-${netDmg} HP`, '#ff003c');
            if (player.health < 30) {
              // low health emergency trigger beeps
              playWarningAlarm();
            }
          }

          // Check Player Death
          if (player.health <= 0) {
            createExplosionEffect(player.x + player.width/2, player.y + player.height/2, 'large');
            setScreen('gameover');
          }
        }
      }
    });

    // Enemy Collides Directly space to space with Player (Kamikaze contact)
    enemies.forEach(e => {
      if (e.health > 0 && 
          e.x > player.x && e.x < player.x + player.width && 
          e.y + e.height > player.y && e.y < player.y + player.height) {
        
        // Instant drone death
        e.health = 0;
        
        if (player.invincibilityActive) return;

        stateRef.current.shakeIntensity = 15;
        playHit();

        const collisionDamage = e.type === 'boss' ? 45 : e.type === 'tank' ? 30 : 15;
        
        if (player.shieldActive && player.shield > 0) {
          player.shield = Math.max(0, player.shield - collisionDamage);
          addDamageIndicator(player.x, player.y - 15, `-${collisionDamage} HULL IMPACT`, '#00f7ff');
        } else {
          player.health = Math.max(0, player.health - collisionDamage);
          addDamageIndicator(player.x, player.y - 15, `-${collisionDamage} CRASH`, '#ffd000');
        }

        if (player.health <= 0) {
          createExplosionEffect(player.x + player.width/2, player.y + player.height/2, 'large');
          setScreen('gameover');
        }
      }
    });

    // 9. POWERUPS INTERSECTS WITH PLAYER
    const remainingPowerUps: PowerUp[] = [];
    powerUps.forEach(pu => {
      pu.pulsePhase += 0.1;
      pu.x += pu.vx;
      pu.y += pu.vy;

      // Hover bobbing
      pu.vy = Math.sin(pu.pulsePhase) * 0.4;

      if (pu.x > player.x && pu.x < player.x + player.width && 
          pu.y > player.y && pu.y < player.y + player.height) {
        
        // Claimed!
        playPowerUp();
        addDamageIndicator(player.x + 20, player.y - 20, `${pu.type.replace('_', ' ').toUpperCase()}`, '#00ff4c');

        switch(pu.type) {
          case 'health_pack':
            player.health = Math.min(player.maxHealth, player.health + 35);
            break;
          case 'shield':
            player.shield = player.maxShield; // immediate full charge
            break;
          case 'rapid_fire':
            player.rapidFireActive = true;
            player.rapidFireTimer = 10000; // 10 secs
            break;
          case 'triple_missiles':
            player.tripleMissilesActive = true;
            player.tripleMissilesTimer = 10000; // 10 secs
            break;
          case 'emp':
            triggerEmpBlast();
            break;
          case 'invincibility':
            player.invincibilityActive = true;
            player.invincibilityTimer = 6000; // 6 secs glow shield
            break;
        }
      } else if (pu.x > -50) {
        remainingPowerUps.push(pu);
      }
    });
    stateRef.current.powerUps = remainingPowerUps;

    // 10. CLEAN / UPDATE ACTIVE PARTICLES
    const livingParticles: Particle[] = [];
    particles.forEach(p => {
      p.life += dt / 16.66;
      p.x += p.vx;
      p.y += p.vy;
      p.alpha = Math.max(0, 1 - p.life / p.maxLife);

      if (p.life < p.maxLife) {
        livingParticles.push(p);
      }
    });
    stateRef.current.particles = livingParticles;

    // 11. CLEAN / UPDATE FLOAT INDICATORS
    const livingIndicators: DamageIndicator[] = [];
    damageIndicators.forEach(di => {
      di.life -= dt;
      di.y -= 0.65 * (dt / 16.66); // Slide upward
      di.alpha = Math.max(0, di.life / 800);

      if (di.life > 0) {
        livingIndicators.push(di);
      }
    });
    stateRef.current.damageIndicators = livingIndicators;

    // Trigger update to the HUD component
    onPlayerStatsUpdate(
      player.health, player.maxHealth, player.shield, player.maxShield, 
      player.missiles, player.maxMissiles, player.xp, player.xpNeeded, player.combo
    );
  };

  // Canvas drawing renderer
  const renderGame = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { 
      player, enemies, projectiles, particles, powerUps, 
      damageIndicators, starScroll, bgScroll, shakeIntensity 
    } = stateRef.current;

    // Apply active screen shake transforms inside frame parameters (impact feedback)
    ctx.save();
    if (shakeIntensity > 0.1) {
      const shakeX = (Math.random() * 2 - 1) * shakeIntensity;
      const shakeY = (Math.random() * 2 - 1) * shakeIntensity;
      ctx.translate(shakeX, shakeY);
    }

    // 1. CLEAR & FILL SPACE DEPACTS BACKGROUND
    ctx.fillStyle = '#060a12'; // beautiful deep interstellar slate blue
    ctx.fillRect(0, 0, dimensions.width, dimensions.height);

    // Dynamic grid line accents
    ctx.strokeStyle = 'rgba(25, 45, 80, 0.12)';
    ctx.lineWidth = 1;
    const gridSize = 45;
    const xGridOffset = -bgScroll % gridSize;
    for (let x = xGridOffset; x < dimensions.width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, dimensions.height);
      ctx.stroke();
    }
    // Static horizontal lanes
    for (let y = 0; y < dimensions.height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(dimensions.width, y);
      ctx.stroke();
    }

    // 2. PARALLAX DISTANT MOUNTAINS/CITY PARALAX LAYER
    ctx.fillStyle = '#0b1326'; // background silhouette
    ctx.beginPath();
    ctx.moveTo(0, dimensions.height);
    // Draw procedural hilly/mountain background repeating
    for (let i = 0; i <= 20; i++) {
      const px = (i * dimensions.width) / 15 - bgScroll;
      // deterministic mountain waves
      const py = dimensions.height - 40 - Math.sin(i * 0.8) * 20 - Math.cos(i * 1.5) * 10;
      ctx.lineTo(px, py);
    }
    ctx.lineTo(dimensions.width, dimensions.height);
    ctx.closePath();
    ctx.fill();

    // Secondary midground silhouette
    ctx.fillStyle = '#101c36'; 
    ctx.beginPath();
    ctx.moveTo(0, dimensions.height);
    for (let i = 0; i <= 20; i++) {
      const px = (i * dimensions.width) / 10 - starScroll;
      const py = dimensions.height - 25 - Math.sin(i * 1.3) * 12 - Math.cos(i * 0.4) * 5;
      ctx.lineTo(px, py);
    }
    ctx.lineTo(dimensions.width, dimensions.height);
    ctx.closePath();
    ctx.fill();

    // Draw bottom battlefield ground border/plate
    ctx.fillStyle = '#14141d';
    ctx.fillRect(0, dimensions.height - 18, dimensions.width, 18);
    ctx.fillStyle = '#01e5ff';
    ctx.fillRect(0, dimensions.height - 18, dimensions.width, 2); // tech grid neon wire floor

    // 3. PARALLAX STARS/CLOUDS
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    for (let i = 0; i < 35; i++) {
      const starX = (i * 53) % dimensions.width;
      const starY = (i * 29) % (dimensions.height - 50);
      // scroll calculations
      const sx = (starX - starScroll + dimensions.width) % dimensions.width;
      ctx.fillRect(sx, starY, (i % 3) + 1, (i % 3) + 1);
    }

    // 4. DRAW POWERUPS
    powerUps.forEach(pu => {
      ctx.save();
      ctx.translate(pu.x, pu.y);
      
      // glowing aura circles
      const glowGrad = ctx.createRadialGradient(0, 0, 2, 0, 0, 16);
      let color = '#00ff55';
      if (pu.type === 'shield' || pu.type === 'invincibility') color = '#00dfff';
      if (pu.type === 'emp') color = '#df00ff';
      if (pu.type === 'triple_missiles' || pu.type === 'rapid_fire') color = '#ff9900';

      glowGrad.addColorStop(0, color);
      glowGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = glowGrad;
      ctx.beginPath();
      ctx.arc(0, 0, 16, 0, Math.PI * 2);
      ctx.fill();

      // bounding box design
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.8;
      ctx.strokeRect(-8, -8, 16, 16);

      // Icon initial letter
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 11px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const char = pu.type[0].toUpperCase();
      ctx.fillText(char === 'H' ? '♥' : char, 0, 0.5);

      ctx.restore();
    });

    // 5. DRAW ENEMIES
    enemies.forEach(e => {
      ctx.save();
      ctx.translate(e.x + e.width / 2, e.y + e.height / 2);
      ctx.rotate(e.angle);

      // Flash enemy when hurt/attacked
      const isDamaged = e.health < e.maxHealth;
      
      // Draw various specific shapes based on type
      if (e.type === 'basic_drone') {
        // Futuristic Drone geometric style
        ctx.fillStyle = '#1e1a1d';
        ctx.strokeStyle = isDamaged ? '#ff2a2a' : '#ff4a4a';
        ctx.lineWidth = 2;
        
        ctx.beginPath();
        ctx.moveTo(-16, -10);
        ctx.lineTo(16, 0);
        ctx.lineTo(-16, 10);
        ctx.lineTo(-8, 0);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Eye glow
        ctx.fillStyle = '#ff1100';
        ctx.beginPath();
        ctx.arc(4, 0, 3, 0, Math.PI * 2);
        ctx.fill();
      } 
      else if (e.type === 'helicopter') {
        // Combat Copter chopper
        ctx.fillStyle = '#2c332e';
        ctx.strokeStyle = '#22c55e';
        ctx.lineWidth = 2;
        // Cockpit base
        ctx.fillRect(-20, -10, 36, 20);
        ctx.strokeRect(-20, -10, 36, 20);
        // tail rotor structure
        ctx.beginPath();
        ctx.moveTo(-20, 0);
        ctx.lineTo(-34, -4);
        ctx.lineTo(-34, 4);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // spinning green rotor lines
        ctx.strokeStyle = 'rgba(34, 197, 94, 0.7)';
        ctx.beginPath();
        const spin = Math.sin(performance.now() * 0.15) * 22;
        ctx.moveTo(-10 + spin, -15);
        ctx.lineTo(-10 - spin, -15);
        ctx.stroke();
      } 
      else if (e.type === 'fighter') {
        // High fidelity alien jet interceptor
        ctx.fillStyle = '#1c1b26';
        ctx.strokeStyle = '#e11d48';
        ctx.lineWidth = 2;

        ctx.beginPath();
        ctx.moveTo(20, 0);
        ctx.lineTo(-16, -16);
        ctx.lineTo(-8, 0);
        ctx.lineTo(-16, 16);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Red laser side wings
        ctx.fillStyle = '#e11d48';
        ctx.fillRect(-12, -20, 4, 3);
        ctx.fillRect(-12, 17, 4, 3);
      } 
      else if (e.type === 'launcher') {
        // Heavy vertical missile mobile launcher unit
        ctx.fillStyle = '#2d2d38';
        ctx.strokeStyle = '#eab308';
        ctx.lineWidth = 2.5;

        // Base frame
        ctx.fillRect(-18, -15, 36, 30);
        ctx.strokeRect(-18, -15, 36, 30);
        // Rocket barrel launcher Tubes
        ctx.fillStyle = '#4b5563';
        ctx.fillRect(-6, -12, 18, 6);
        ctx.fillRect(-6, 6, 18, 6);
      } 
      else if (e.type === 'tank') {
        // Giant tracked war machine tank
        ctx.fillStyle = '#2e3033';
        ctx.strokeStyle = '#a855f7';
        ctx.lineWidth = 3;

        // Tracks bottom
        ctx.fillRect(-25, 4, 50, 12);
        ctx.strokeRect(-25, 4, 50, 12);
        // Main metal cockpit turret
        ctx.fillStyle = '#3f4147';
        ctx.beginPath();
        ctx.arc(0, -2, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        // Spiky weapon muzzle facing left
        ctx.fillStyle = '#7a7d85';
        ctx.fillRect(-28, -6, 16, 8);
      } 
      else if (e.type === 'boss') {
        // MASSIVE SKY OMEGA CRUISER BOSS
        // Draw majestic layered panels
        ctx.fillStyle = '#161924';
        ctx.strokeStyle = '#ff0055';
        ctx.lineWidth = 3.5;

        ctx.beginPath();
        ctx.moveTo(70, 0);
        ctx.lineTo(-20, -45);
        ctx.lineTo(-70, -35);
        ctx.lineTo(-50, 0);
        ctx.lineTo(-70, 35);
        ctx.lineTo(-20, 45);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Glowing Core center
        const coreRad = 12 + Math.sin(performance.now() * 0.01) * 3;
        const radial = ctx.createRadialGradient(20, 0, 2, 20, 0, coreRad);
        radial.addColorStop(0, '#ff003c');
        radial.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = radial;
        ctx.beginPath();
        ctx.arc(20, 0, coreRad, 0, Math.PI * 2);
        ctx.fill();

        // Side thrusters flare red
        ctx.fillStyle = '#ff1d00';
        for (let th = -28; th <= 28; th += 56) {
          ctx.beginPath();
          ctx.moveTo(-68, th - 6);
          ctx.lineTo(-84 - (Math.random() * 15), th);
          ctx.lineTo(-68, th + 6);
          ctx.closePath();
          ctx.fill();
        }
      }

      ctx.restore();

      // Show tiny individual health bar immediately overhead for major target enemies
      if (e.type !== 'basic_drone' && e.health > 0) {
        const barW = e.width * 1.1;
        const barH = 3.5;
        const bx = e.x + e.width / 2 - barW / 2;
        const by = e.y - 12;

        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.fillRect(bx, by, barW, barH);

        const hpPct = Math.max(0, e.health / e.maxHealth);
        let barColor = '#32cd32'; // Green default
        if (e.type === 'boss') barColor = '#e11d48'; // red
        else if (e.type === 'launcher') barColor = '#eab308'; // orange

        ctx.fillStyle = barColor;
        ctx.fillRect(bx, by, barW * hpPct, barH);
      }
    });

    // 6. DRAW PROJECTILES
    projectiles.forEach(p => {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);

      if (p.type === 'bullet') {
        // Laser bullet style (glowing blue-white line vector)
        ctx.strokeStyle = '#00f2ff';
        ctx.lineWidth = 3.5;
        ctx.shadowColor = '#00f2ff';
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.moveTo(-p.width / 2, 0);
        ctx.lineTo(p.width / 2, 0);
        ctx.stroke();
      } 
      else if (p.type === 'enemy_bullet') {
        // Red laser bullets
        ctx.strokeStyle = '#ff0051';
        ctx.lineWidth = 3.0;
        ctx.shadowColor = '#ff0051';
        ctx.shadowBlur = 6;
        ctx.beginPath();
        ctx.moveTo(-p.width / 2, 0);
        ctx.lineTo(p.width / 2, 0);
        ctx.stroke();
      } 
      else if (p.type === 'plasma') {
        // Glowing atomic ball
        const radGrad = ctx.createRadialGradient(0, 0, 1, 0, 0, p.width/2);
        radGrad.addColorStop(0, '#fffffd');
        radGrad.addColorStop(0.3, '#bc00ff');
        radGrad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = radGrad;
        ctx.beginPath();
        ctx.arc(0, 0, p.width/2, 0, Math.PI*2);
        ctx.fill();
      } 
      else {
        // Missile style (player or enemy: cylinder body with glowing thruster back)
        const isPlayerMissile = p.type === 'missile';
        
        ctx.fillStyle = isPlayerMissile ? '#e1e3e6' : '#d2222d';
        ctx.strokeStyle = '#0a0a0a';
        ctx.lineWidth = 1;
        // Missile head
        ctx.fillRect(-p.width/2, -p.height/2, p.width, p.height);
        
        // triangular pointed missile tip
        ctx.fillStyle = isPlayerMissile ? '#00e5ff' : '#000000';
        ctx.beginPath();
        ctx.moveTo(p.width/2, -p.height/2);
        ctx.lineTo(p.width/2 + 6, 0);
        ctx.lineTo(p.width/2, p.height/2);
        ctx.closePath();
        ctx.fill();

        // flame exhausting tail
        ctx.fillStyle = '#ff5100';
        ctx.beginPath();
        ctx.moveTo(-p.width/2, -p.height/3);
        ctx.lineTo(-p.width/2 - 8 - Math.random() * 4, 0);
        ctx.lineTo(-p.width/2, p.height/3);
        ctx.closePath();
        ctx.fill();
      }

      ctx.restore();
    });

    // 7. DRAW ACTIVE TARGET-LOCKING INDICATOR UI
    // Target-lock visuals directly matching current auto targeted enemy
    const lockedEnemy = enemies.find(e => e.id === lockedTargetId);
    if (lockedEnemy && lockedEnemy.health > 0) {
      ctx.save();
      ctx.translate(lockedEnemy.x + lockedEnemy.width / 2, lockedEnemy.y + lockedEnemy.height / 2);
      
      const rot = (performance.now() * 0.003) % (Math.PI * 2);
      ctx.rotate(rot);

      // Lock on glowing green brackets
      ctx.strokeStyle = '#00ff40';
      ctx.lineWidth = 1.8;
      ctx.shadowColor = '#00ff40';
      ctx.shadowBlur = 6;
      
      const size = Math.max(lockedEnemy.width, lockedEnemy.height) * 0.85 + 4;

      // Draw standard sleek corners
      for (let side = 0; side < 4; side++) {
        ctx.rotate(Math.PI / 2);
        ctx.beginPath();
        ctx.moveTo(size - 6, size);
        ctx.lineTo(size, size);
        ctx.lineTo(size, size - 6);
        ctx.stroke();
      }

      // Small visual locked text
      ctx.rotate(-rot); // unrotate text
      ctx.fillStyle = '#00ff40';
      ctx.font = 'bold 9px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText('LOCK ON', 0, -size - 4);

      ctx.restore();
    }

    // 8. DRAW ACTIVE PARTICLES
    particles.forEach(p => {
      ctx.save();
      ctx.globalAlpha = p.alpha;
      ctx.fillStyle = p.color;

      if (p.type === 'shield_bubble') {
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    });

    // 9. DRAW THE PLAYER AIRPLANE
    ctx.save();
    ctx.translate(player.x + player.width / 2, player.y + player.height / 2);

    // Speed tilt calculation slightly based on keystrokes
    const tilt = stateRef.current.controls.up ? -0.12 : stateRef.current.controls.down ? 0.12 : 0;
    ctx.rotate(tilt);

    // Glowing invincibility golden capsule or normal flight outline
    if (player.invincibilityActive) {
      ctx.shadowColor = '#ffbb00';
      ctx.shadowBlur = 12 + Math.sin(performance.now() * 0.02) * 4;
      ctx.strokeStyle = '#ff9900';
      ctx.lineWidth = 2.5;

      // Draw bubble capsule
      ctx.beginPath();
      ctx.arc(0, 0, player.width * 0.75, 0, Math.PI * 2);
      ctx.stroke();
    } else if (player.shieldActive && player.shield > 0) {
      ctx.shadowColor = '#00dfff';
      ctx.shadowBlur = 8 + Math.sin(performance.now() * 0.01) * 3;
      ctx.strokeStyle = 'rgba(0, 223, 255, 0.55)';
      ctx.lineWidth = 2.0;

      ctx.beginPath();
      ctx.arc(0, 0, player.width * 0.7, 0, Math.PI * 2);
      ctx.stroke();
    }

    // FIGHTER JET MAIN BODY DRAWING
    ctx.fillStyle = '#cfd4de'; // metallic chrome base
    ctx.strokeStyle = '#1e2d4a';
    ctx.lineWidth = 2;

    // Cockpit & wings paths
    ctx.beginPath();
    ctx.moveTo(player.width / 2 + 5, 0); // nose tip
    ctx.lineTo(-player.width / 4, -player.height / 3); // top wing junction
    ctx.lineTo(-player.width / 2, -player.height * 0.9); // left wing peak
    ctx.lineTo(-player.width / 3, -player.height / 5); // left wing back edge
    ctx.lineTo(-player.width / 2, 0); // exhaust center
    ctx.lineTo(-player.width / 3, player.height / 5); // right wing back edge
    ctx.lineTo(-player.width / 2, player.height * 0.9); // right wing peak
    ctx.lineTo(-player.width / 4, player.height / 3); // right wing junction
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Secondary trim panels (aesthetic vector outlines)
    ctx.fillStyle = '#ff4a00'; // high-contrast fighter marks
    ctx.beginPath();
    ctx.moveTo(0, -player.height / 4);
    ctx.lineTo(-player.width / 3, -player.height * 0.7);
    ctx.lineTo(-player.width / 4, -player.height / 4);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#ff4a00';
    ctx.beginPath();
    ctx.moveTo(0, player.height / 4);
    ctx.lineTo(-player.width / 3, player.height * 0.7);
    ctx.lineTo(-player.width / 4, player.height / 4);
    ctx.closePath();
    ctx.fill();

    // Cockpit windshield piece (glowing cyan glass)
    ctx.fillStyle = '#00d0ff'; // Neon blue/cyan pilot dome
    ctx.beginPath();
    ctx.ellipse(8, 0, 10, 4, 0, 0, Math.PI * 2);
    ctx.fill();

    // Jet engine nozzle flame (dynamic pulsating engine fire)
    ctx.fillStyle = player.speedBoostActive ? '#00e5ff' : '#ff7300';
    ctx.beginPath();
    const nozzleHeight = 5;
    const flameStretch = (player.speedBoostActive ? 22 : 12) + Math.random() * 8;
    ctx.moveTo(-player.width / 2, -nozzleHeight);
    ctx.lineTo(-player.width / 2 - flameStretch, 0);
    ctx.lineTo(-player.width / 2, nozzleHeight);
    ctx.closePath();
    ctx.fill();

    ctx.restore();

    // 10. DRAW FLOATING DAMAGE INDICATORS & GAME HUD EVENTS
    damageIndicators.forEach(di => {
      ctx.save();
      ctx.globalAlpha = di.alpha;
      ctx.fillStyle = di.color;
      ctx.font = 'bold 13px "JetBrains Mono", monospace';
      ctx.textAlign = 'center';
      
      // glow filter to float text readability
      ctx.shadowColor = '#000000';
      ctx.shadowBlur = 4;

      ctx.fillText(di.text, di.x, di.y);
      ctx.restore();
    });

    // Restore active translate state settings safely
    ctx.restore();
  };

  return (
    <div 
      className="relative w-full overflow-hidden select-none bg-slate-950 rounded-xl border border-slate-800 shadow-2xl"
      ref={containerRef}
      style={{ minHeight: '340px' }}
    >
      <canvas 
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        className="block mx-auto cursor-crosshair bg-slate-950 w-full"
      />
    </div>
  );
};
